import _ from 'lodash';
import {
    HINT_ADD,
    StoreAndForwardChangeEvent,
    StoreAndForwardJobStore,
} from 'app/shared/store_and_forward/StoreAndForwardJobStore';
import DebugService from 'app/shared/debug.service';
import { PromisedTimeout } from 'app/shared/utilities/PromisedTimeout';
import {
    CRUDRunnerResult,
    StoreAndForwardCRUDConflictError,
    StoreAndForwardCRUDRunner,
} from 'app/shared/store_and_forward/CRUDRunner';

const log = (...args) => DebugService.log('StoreAndForwarder', ...args);

export class StoreAndForwarder {
    private jobStore: StoreAndForwardJobStore;
    private crudRunner: StoreAndForwardCRUDRunner;
    private coolOffTimeoutMs: number;
    private running: boolean;
    private promisedTimeout: PromisedTimeout;

    constructor(
        jobStore: StoreAndForwardJobStore,
        crudRunner: StoreAndForwardCRUDRunner,
        coolOffTimeoutMs: number,
    ) {
        if (!jobStore) {
            throw new Error('jobStore is nil');
        }
        if (!crudRunner) {
            throw new Error('crudRunner is nil');
        }
        if (!_.isNumber(coolOffTimeoutMs) || coolOffTimeoutMs <= 0) {
            throw new Error('coolOffTimeoutMs must be a positive number');
        }
        this.jobStore = jobStore;
        this.crudRunner = crudRunner;
        this.coolOffTimeoutMs = coolOffTimeoutMs;
        this.running = false;
        this.promisedTimeout = null;

        jobStore.addOnChangeListener((ev: StoreAndForwardChangeEvent) => {
            // We've added a job. If it's the first job in the queue
            // and we're currently in a cool off timeout, cancel the timeout
            // and start the job immediately.
            if (ev.jobCount === 1 && ev.hint === HINT_ADD && this.promisedTimeout !== null) {
                log('job store job added');
                this.clearTimeoutIfWaiting();
            }
        });
    }

    /**
     * Returns a promise that resolves after stop() is called
     * @returns {Promise<any>}
     */
    start(): Promise<any> {
        if (this.running) {
            throw new Error(`This StoreAndForwarder is already running!`);
        } else {
            this.running = true;
            return this.run();
        }
    }

    stop(): void {
        this.running = false;
    }

    private async run(): Promise<any> {
        try {
            while (this.running) {
                if (!this.jobStore.isJobRunning()) {
                    const job = this.jobStore.getJobToRun();
                    if (job) {
                        try {
                            log('before run job');
                            const result: CRUDRunnerResult = await this.crudRunner.runJob(job);
                            log('run job success');
                            // If we reached here the job was successful...
                            this.jobStore.jobSuccess(job, result.resource);
                        } catch (err) {
                            console.error(err);
                            this.jobStore.jobFailed(job, err);
                            log('run job failed, cooling off....');
                            if (err instanceof StoreAndForwardCRUDConflictError) {
                                // There has been a conflict error
                                // Inform the job store that some IDs need to change...
                                this.jobStore.idChange(
                                    err.localObjId,
                                    err.apiObjId,
                                    err.imsItemType,
                                );
                            }
                            await this.timeout(this.coolOffTimeoutMs);
                        }
                    } else {
                        log('no job to run, cooling off....');
                        await this.timeout(this.coolOffTimeoutMs);
                        log('after no job cool off');
                    }
                } else {
                    log('should not get here but cool off anyway');
                    await this.timeout(this.coolOffTimeoutMs);
                }
            }
        } catch (err) {
            log('error in store and forward run', err);
        }
    }

    private clearTimeoutIfWaiting() {
        if (this.promisedTimeout !== null) {
            this.promisedTimeout.triggerEarly();
        }
    }

    private async timeout(ms): Promise<any> {
        if (this.promisedTimeout !== null) {
            throw new Error('Timeout is already running');
        }
        try {
            this.promisedTimeout = new PromisedTimeout(ms);
            return await this.promisedTimeout.promise;
        } catch (err) {
            // do nothing, it's just been cancelled
            log('timeout cancelled');
        } finally {
            this.promisedTimeout = null;
            log('timeout cancelled finally block');
        }
    }
}
